import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import PanelWithHelp from '../PanelWithHelp';
import IconButton from '@material-ui/core/IconButton';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { saveGame, deleteGame } from '../../services/Master';
import { getHelpUrl } from '../../services/shared';
import { useAuthContext, changeLanguage } from '../../contexts/AuthContext';
import { useFocus } from '../../helpers/Dom';
import ParameterControl from '../ParameterControl';
import ConfirmationDialog from '../Dialog/Confirmation';
import HeaderButtonContainer from './HeaderButtonContainer';
import { kassimBlue } from '../../style/theme';

const MainContainer = styled.div`
    height: 100%;
    overflow-x: auto;
`;

const PanelContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const ParameterGroup = styled(PanelWithHelp).attrs({ elevation: 2 })`
    padding: 4px 16px 8px 16px;
    margin: 8px;
`;

const ErrorPanel = styled(ParameterGroup)`
    background-color: #d32f2f;
    color: #ffffff;
`;

const InfoPanel = styled(ParameterGroup)`
    flex-grow: 1;
    flex-basis: 100%;
`;

const PanelLabel = styled.h3`
    margin-top: 0px;
    color: ${kassimBlue};
    border-bottom: 2px solid ${kassimBlue};
    padding-right: 16px;
`;

const CopyRegisterLinkParameter = styled(ParameterControl)`
    align-items: center;
`;

const organizeControlProps = (config, gameData) => {
    const panels = {};
    const parameters = { ...gameData };
    if (config)
        for (const [paramKey, param] of Object.entries(_.cloneDeep(config))) {
            if (paramKey.startsWith('_')) {
                continue;
            }
            const { type } = param;
            if (type !== 'custom' && type !== 'admin') {
                if (typeof panels[type] === 'undefined') {
                    panels[type] = [];
                }
                panels[type].push({
                    ...param,
                    defaultValue: param.default,
                    key: paramKey,
                });
                if (gameData?.[paramKey] || gameData?.[paramKey] === 0) {
                    parameters[paramKey] = _.cloneDeep(gameData[paramKey]);
                } else if (param.default || param.default === 0) {
                    parameters[paramKey] = param.default;
                } else {
                    parameters[paramKey] = '';
                }
                if (
                    param.enableable &&
                    !(
                        typeof parameters[paramKey] === 'object' &&
                        parameters[paramKey] !== null &&
                        parameters[paramKey].hasOwnProperty('enabled')
                    )
                ) {
                    if (
                        typeof parameters[paramKey] === 'object' &&
                        parameters[paramKey] !== null &&
                        parameters[paramKey].hasOwnProperty('value')
                    ) {
                        parameters[paramKey].enabled = false;
                    } else {
                        parameters[paramKey] = {
                            value: parameters[paramKey],
                            enabled: false,
                        };
                    }
                }
            }
        }
    return { panels, parameters };
};

const replaceEmptyWithNull = (settings) => {
    return Object.entries(settings).reduce((prev, [key, value]) => {
        let val;
        if (value === '') {
            val = null;
        } else if (value?.value === '') {
            val = { ...value, value: null };
        } else {
            val = value;
        }
        return { ...prev, [key]: val };
    }, {});
};

const GameSettings = ({
    className,
    gameData,
    parameterConfig,
    onSave,
    onDelete,
}) => {
    const { t } = useTranslation();
    const [panelData, setPanelData] = useState({});
    const { auth, dispatch } = useAuthContext();
    const [settings, setSettings] = useState();
    const [pristine, setPristine] = useState(true);
    const [inputWarnings, setInputWarnings] = useState({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [titleInputRef, setTitleInputFocus] = useFocus();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();

    const setParameter = (key, value) => {
        setPristine(false);
        setSettings((prev) => ({ ...prev, [key]: value }));
        setInputWarnings((prev) => ({ ...prev, [key]: null }));
    };

    useEffect(() => {
        if (typeof settings?.['@language'] !== 'undefined') {
            dispatch(changeLanguage(settings['@language']));
        }
    }, [settings?.['@language']]);

    useEffect(() => {
        if (gameData) {
            setPristine(true);
            const { panels, parameters } = organizeControlProps(
                parameterConfig,
                gameData
            );
            setPanelData(panels);
            setSettings({
                ...parameters,
                // name: gameData.name || newGameName,
                name: gameData.name || '',
                info: gameData.info || '',
                '@token': gameData['@token'],
            });
        } else {
            setSettings(null);
            setPanelData({});
            setPristine(true);
        }
    }, [parameterConfig, gameData]);

    const handleSaveGame = () => {
        if (pristine) {
            console.warn('Nothing changed');
        }
        if (!settings?.name) {
            setInputWarnings((prev) => ({
                ...prev,
                name: t('error.noName'),
            }));
            enqueueSnackbar(t('error.noName'), {
                variant: 'error',
            });
            setTitleInputFocus();
        } else {
            setSubmitting(true);
            saveGame(auth.token, replaceEmptyWithNull(settings))
                .then((returnData) => {
                    if (onSave) {
                        onSave(settings.name);
                    }
                    enqueueSnackbar(t('confirmation.gameSaved'), {
                        variant: 'success',
                    });
                })
                .catch((err) => {
                    console.warn(err);
                    enqueueSnackbar(
                        <div>
                            <p>{t('error.gameNotSaved')}</p>
                            <p>{err.message}</p>
                        </div>,
                        {
                            variant: 'error',
                        }
                    );
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    };

    const handleClickDeleteGame = () => {
        setShowDeleteConfirmation(true);
    };
    const handleDeleteGame = () => {
        setShowDeleteConfirmation(false);
        setSubmitting(true);
        deleteGame(auth.token, settings.name)
            .then(() => {
                if (onDelete) {
                    onDelete();
                }
            })
            .catch((err) => {
                console.warn(err);
                enqueueSnackbar(
                    <div>
                        <p>{t('error.gameNotDeleted')}</p>
                        <p>{err.message}</p>
                    </div>,
                    {
                        variant: 'error',
                    }
                );
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const getRegisterUrl = (gameToken) => {
        const baseUrl = window.location.href.replace(
            new RegExp(location.pathname + '$'),
            ''
        );
        return `${baseUrl}/student/register?game=${gameToken}`;
    };
    const registerUrl = getRegisterUrl(settings?.['@token']);
    const copyRegisterUrlButton = (
        <CopyToClipboard text={registerUrl}>
            <IconButton>
                <AssignmentIcon fontSize="small" />
            </IconButton>
        </CopyToClipboard>
    );
    if (!gameData) return null;
    if (!settings) return null;

    const help = parameterConfig?.['@help']?.master;

    const getFullHelpUrl = (paramUrl) =>
        getHelpUrl(paramUrl, parameterConfig, auth.languageId);

    return (
        <div className={className}>
            <HeaderButtonContainer
                actions={[
                    {
                        key: 'save',
                        label: t('buttons.save'),
                        // disabled: pristine || !settings?.name,
                        disabled: submitting || gameData.configError,
                        onClick: handleSaveGame,
                    },
                    {
                        key: 'delete',
                        label: t('buttons.delete'),
                        disabled: submitting,
                        onClick: handleClickDeleteGame,
                    },
                ]}
            />
            <MainContainer>
                {gameData.configError ? (
                    <ErrorPanel>
                        {t('error.configurationErrorText', {
                            returnObjects: true,
                        }).map((line, i) => (
                            <p key={i}>{line}</p>
                        ))}
                    </ErrorPanel>
                ) : (
                    <PanelContainer>
                        <InfoPanel
                            key="info"
                            helpUrl={getFullHelpUrl(help?.general)}
                        >
                            <PanelLabel>General</PanelLabel>
                            <ParameterControl
                                key={`name`}
                                id={`name`}
                                value={settings?.name}
                                placeholder={
                                    parameterConfig?.name?.default ||
                                    t('gamesettings.name_placeholder')
                                }
                                url={getFullHelpUrl(parameterConfig?.name?.url)}
                                text={['Naam', 'Name']}
                                control="textbox"
                                onChange={(val) => setParameter('name', val)}
                                autoFocus
                                ref={titleInputRef}
                                error={!!inputWarnings.name}
                                helperText={inputWarnings.name}
                            />
                            <ParameterControl
                                id={`info`}
                                value={settings?.info}
                                placeholder={
                                    parameterConfig?.info?.default ||
                                    t('gamesettings.info_placeholder')
                                }
                                url={getFullHelpUrl(parameterConfig?.info?.url)}
                                text={['Info', 'Info']}
                                control="textarea"
                                onChange={(val) => setParameter('info', val)}
                            />
                            {settings?.['@token'] && (
                                <CopyRegisterLinkParameter
                                    id={`registerUrl`}
                                    value={settings?.['@token']}
                                    text={[
                                        'Kopieer registratielink',
                                        'Copy register url',
                                    ]}
                                    control="custom"
                                    customControl={copyRegisterUrlButton}
                                />
                            )}
                        </InfoPanel>
                        {settings &&
                            Object.keys(panelData).map((panelKey) => {
                                const panel = panelData[panelKey];
                                return (
                                    <ParameterGroup
                                        key={panelKey}
                                        helpUrl={getFullHelpUrl(
                                            help?.[panelKey]
                                        )}
                                    >
                                        <PanelLabel>
                                            {t(
                                                `gamesettings.panels.${panelKey}`
                                            )}
                                        </PanelLabel>
                                        {panel.map((param) => {
                                            return (
                                                <ParameterControl
                                                    id={`${param.key}`}
                                                    {...param}
                                                    url={getFullHelpUrl(
                                                        param.url
                                                    )}
                                                    value={settings[param.key]}
                                                    readonly={false}
                                                    onChange={(val) =>
                                                        setParameter(
                                                            param.key,
                                                            val
                                                        )
                                                    }
                                                />
                                            );
                                        })}
                                    </ParameterGroup>
                                );
                            })}
                    </PanelContainer>
                )}
            </MainContainer>
            <ConfirmationDialog
                open={showDeleteConfirmation}
                title="Waarschuwing"
                onCancel={() => {
                    setShowDeleteConfirmation(false);
                }}
                onAccept={handleDeleteGame}
            >
                <p>Game en alle gebruikers worden verwijderd.</p>
                <p>Weet u het zeker?</p>
            </ConfirmationDialog>
        </div>
    );
};
export default GameSettings;
